import { createApi } from '@reduxjs/toolkit/query/react';
import { createInstance } from '../../util/sdkLoader';
import appSettings from '../../config/settings';
import * as apiUtils from '../../util/api';

const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
  ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
  : {};

const sdk = createInstance({
  transitVerbose: appSettings.sdk.transitVerbose,
  clientId: appSettings.sdk.clientId,
  secure: appSettings.usingSSL,
  typeHandlers: apiUtils.typeHandlers,
  ...baseUrl,
  ...assetCdnBaseUrl,
});

export const sdkBaseQuery = async ({ method, args }) => {
  try {
    // Split the method string by "." to handle nested methods
    const methodParts = method.split('.');
    let target = sdk;

    // Traverse the method path
    for (const part of methodParts) {
      if (!target[part]) {
        throw new Error(`SDK method "${method}" not found`);
      }
      target = target[part];
    }

    // Ensure the target is a function before calling it
    if (typeof target !== 'function') {
      throw new Error(`SDK method "${method}" is not a function`);
    }

    const response = await target(...args);
    return { data: response };
  } catch (error) {
    return { error: { status: 'CUSTOM_ERROR', message: error.message } };
  }
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: sdkBaseQuery,
  endpoints: builder => {
    return {
      getListingsByProviderWithImages: builder.query({
        query: providerId => ({
          method: 'listings.query',
          args: [
            {
              authorId: providerId,
              include: ['images'],
              'fields.image': ['variants.square-small2x'],
            },
          ],
        }),
      }),
      getListingsByProvider: builder.query({
        query: providerId => ({
          method: 'listings.query',
          args: [
            {
              authorId: providerId,
            },
          ],
        }),
      }),
      getListingById: builder.query({
        query: id => {
          return {
            method: 'listings.show',
            args: [
              {
                id,
              },
            ],
          };
        },
      }),
      getClassScheduleByListingId: builder.query({
        query: ({ listingId, start, end }) => ({
          method: 'timeslots.query',
          args: [{ listingId, start, end }],
        }),
      }),
      getUserByUserId: builder.query({
        query: id => ({
          method: 'users.show',
          args: [{ id }],
        }),
      }),
    };
  },
});

export const {
  useGetListingsByProviderWithImagesQuery,
  useGetListingsByProviderQuery,
  useGetListingByIdQuery,
  useGetClassScheduleByListingIdQuery,
  useGetUserByUserIdQuery,
} = apiSlice;
